import React, { useEffect, useState, useRef, FC, PropsWithChildren } from 'react'
import useOnScreen from 'hooks/onScreen'
import SeoContentConnector, { ConnectorProps as SeoContentProps } from '../connectors/SeoContent'
import ClearFiltersConnector, {
  ConnectorProps as ClearFiltersProps,
} from '../connectors/ClearFilters'
import ModalConnector, { ConnectorProps as ModalConnectorProps } from 'connectors/Modal'
import Breadcrumb from '../components/Breadcrumb'
import Header from './Header'
import RelatedSearches from '../components/seoContent/RelatedSearches'
import NarrowYourResults from '../components/seoContent/NarrowYourResults'
import BrowsePagination from './BrowsePagination'
import BrowsePolaroidList from '../components/artworks/BrowsePolaroidList'
import RecommendedArtworksCarousel from './RecommendedCarousel'
import { setAlgoliaQueryIDInLocalStorage } from 'lib/algolia/v2/helpers'
import styles from './Browse.module.scss'
import SEOBlurb from '../components/seoContent/SEOBlurb'
import classNames from 'classnames'
import useIsMobile from 'hooks/useIsMobile'
import { slugify } from 'lib/slugify'
import Modal from 'components/SA/Modal/v2'
import BrowseFilters from './BrowseFilters'
import BrowseFiltersChips from '../components/header/BrowseFiltersChips'
import { updateClientSideUrl } from 'components/SA/Facets/helpers/url'
import { BROWSE_NEXT_APP_ROUTE } from '../helpers/constants'
import FilterButton from '../components/header/FilterButton'
import { useGetAlgoliaData } from '../hooks/useGetAlgoliaData'

const BROWSE_FILTER_MODAL_NAME = 'browseFilters'

type BrowseProps = ModalConnectorProps & SeoContentProps & ClearFiltersProps

const BrowseFiltersParent: FC<PropsWithChildren> = ({ children }) => {
  const isMobileScreen = useIsMobile()

  if (!isMobileScreen) {
    return children
  }

  return (
    <Modal
      name={BROWSE_FILTER_MODAL_NAME}
      layout='standard'
      styleOverride={styles.mobileFiltersModal}
      isCloseButtonVisible
    >
      <div className={styles.mobileFiltersModalContent}>{children}</div>
    </Modal>
  )
}

const Layout: FC<BrowseProps> = ({
  queryID,
  openModal,
  closeModal,
  hasPrints,
  selectedCategoryFacet,
}) => {
  const { isAlgoliaLoading } = useGetAlgoliaData() // Request Algolia data on client side in the first render as well

  const [shouldLoadComponent, setShouldLoadComponent] = useState(false)
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)

  const elementRef = useRef(null)
  const isMobileScreen = useIsMobile()
  const { isViewable } = useOnScreen(elementRef)

  const handleOpenMobileFilter = () => {
    openModal(BROWSE_FILTER_MODAL_NAME)
  }

  const customClearFilterCallback = () => {
    const mainUrl = `/${slugify(selectedCategoryFacet)}`
    const href = hasPrints ? '/prints' : mainUrl
    updateClientSideUrl(href, BROWSE_NEXT_APP_ROUTE)
  }

  useEffect(() => {
    if (isViewable) {
      setShouldLoadComponent(true)
    }
  }, [isViewable])

  useEffect(() => {
    // store ALGOLIA_QUERY_ID in local storage
    setAlgoliaQueryIDInLocalStorage(queryID)
  })

  useEffect(() => {
    const closeFilterOnDesktop = () => {
      if (window.innerWidth > 999) {
        closeModal()
      }
    }

    window.addEventListener('resize', closeFilterOnDesktop)
    return () => window.removeEventListener('resize', closeFilterOnDesktop)
  }, [])

  return (
    <div className={styles.browseWrapper}>
      <div className={classNames(styles.browsePaddingContainer, styles.breadcrumbHeaderContainer)}>
        <Breadcrumb />
        <Header
          isFiltersVisible={isFiltersVisible}
          onOpenMobileFilter={handleOpenMobileFilter}
          isAlgoliaLoading={isAlgoliaLoading}
        />
        <div className={styles.filterButtonChipsContainer}>
          <div className={styles.desktopFilterButton}>
            {!isMobileScreen && (
              <FilterButton
                onClickFilterButton={() => setIsFiltersVisible((prevValue) => !prevValue)}
                isFiltersVisible={isFiltersVisible}
                isAlgoliaLoading={isAlgoliaLoading}
              />
            )}
          </div>
          <BrowseFiltersChips
            selectedCategoryFacet={selectedCategoryFacet}
            customClearFilterCallback={customClearFilterCallback}
          />
        </div>
      </div>
      <div className={styles.mainContentGrid}>
        <div
          className={classNames(styles.left, {
            [styles.hiddenGrid]: !isFiltersVisible,
          })}
        >
          <BrowseFiltersParent>
            <BrowseFilters
              setIsFiltersVisible={setIsFiltersVisible}
              closeModal={closeModal}
              customClearFilterCallback={customClearFilterCallback}
              isFiltersLoading={isAlgoliaLoading}
            />
          </BrowseFiltersParent>
        </div>
        <div
          className={classNames(styles.right, {
            [styles.fullGrid]: !isFiltersVisible,
          })}
        >
          <BrowsePolaroidList
            isFiltersVisible={isFiltersVisible}
            isAlgoliaLoading={isAlgoliaLoading}
          />
        </div>
      </div>

      <div
        className={classNames(styles.browsePaddingContainer, styles.paginationContainer)}
        ref={elementRef}
      >
        <BrowsePagination />
      </div>

      <div className={styles.browseFooterContainer}>
        <div className={styles.browsePaddingContainer}>
          <RecommendedArtworksCarousel
            shouldLoadComponent={shouldLoadComponent}
            setShouldLoadComponent={setShouldLoadComponent}
          />
        </div>

        <div className={styles.browsePaddingContainer}>
          <NarrowYourResults />
        </div>

        <SEOBlurb />
        <RelatedSearches />
      </div>
    </div>
  )
}

export default SeoContentConnector(ModalConnector(ClearFiltersConnector(Layout)))
